import { Button, Checkbox, Form, Space, Table, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { AppointmentTypeSettings } from '../../types/appointments';
import { ColumnsType } from 'antd/es/table';

interface AppointmentsFormData {
  appointments: AppointmentTypeSettings[];
}

interface AppointmentsConfigProps {
  appointments: AppointmentTypeSettings[];
  setAppointments: (appointments: AppointmentTypeSettings[]) => void;
  apiUrl: string;
  clientId: string;
  accessToken: string;
  setError: (error: string | undefined) => void;
}

export function AppointmentsConfig({
  appointments,
  setAppointments,
  apiUrl,
  clientId,
  accessToken,
  setError,
}: AppointmentsConfigProps) {
  const [form] = Form.useForm<AppointmentsFormData>();
  const [loading, setLoading] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const handleFinish = async (values: AppointmentsFormData) => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch(
        `${apiUrl}/api/settings/${clientId}/appointment-types`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(values.appointments),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update appointment types');
      }

      setAppointments(values.appointments);
      setIsTouched(false);
    } catch (error) {
      console.error('Error updating appointment types:', error);
      setError(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  const renderTitle = (title: string, tooltip: string) => (
    <Space>
      {title}
      <Typography.Text style={{ fontSize: '14px' }} type="secondary">
        <QuestionCircleOutlined style={{ cursor: 'help' }} title={tooltip} />
      </Typography.Text>
    </Space>
  );

  const columns: ColumnsType<AppointmentTypeSettings> = [
    {
      title: renderTitle(
        'Valid for AI',
        'Whether this appointment type can be processed by AI',
      ),
      dataIndex: 'is_valid_for_ai',
      key: 'is_valid_for_ai',
      width: '100px',
      align: 'center',
      render: (_: boolean, record: AppointmentTypeSettings, index: number) => (
        <Form.Item
          name={['appointments', index, 'is_valid_for_ai']}
          style={{ margin: 0, textAlign: 'center' }}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      ),
    },
    {
      title: 'Appointment Name',
      dataIndex: 'name',
      key: 'name',
      width: '200px',
      render: (
        name: string,
        record: AppointmentTypeSettings,
        index: number,
      ) => (
        <>
          <Form.Item
            name={['appointments', index, 'name']}
            style={{ margin: 0, textAlign: 'left' }}
          >
            <Typography.Text>{name}</Typography.Text>
          </Form.Item>
          <Form.Item
            name={['appointments', index, 'id']}
            style={{ margin: 0, textAlign: 'left' }}
          ></Form.Item>
        </>
      ),
    },
  ];

  return (
    <div
      style={{ textAlign: 'left', backgroundColor: 'white', padding: '20px' }}
    >
      <Typography.Title level={2}>Appointment Types</Typography.Title>
      <Typography.Paragraph>
        Configure which appointment types can be processed by AI. If valid for
        AI is not checked, all data will be lost on save
      </Typography.Paragraph>
      <Form
        disabled={loading}
        form={form}
        initialValues={{ appointments }}
        onFinish={handleFinish}
        onValuesChange={() => setIsTouched(true)}
      >
        <Table
          columns={columns}
          dataSource={appointments}
          pagination={false}
          rowKey="id"
        />
        <Space style={{ marginTop: '20px' }}>
          <Button disabled={!isTouched} htmlType="submit" type="primary">
            Save
          </Button>
        </Space>
      </Form>
    </div>
  );
}
